import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { CurrencyCodeSchema } from 'types/currency';
import { CommunicationLocaleSchema } from 'types/locale';
import { z } from 'zod/v4';

import { CurrencyConversionDateSchema } from './use-load-company-profile';

const URL = '/company_profile';

// `vatNumber` is intentionally absent: it is read-only and never written from
// this page. Every field is optional so the card can send a partial update.
const UpdateCompanyProfileVariablesSchema = z.object({
  name: z.string().min(1).optional(),
  companyEmail: z.string().min(1).optional(),
  locale: CommunicationLocaleSchema.optional(),
  currency: CurrencyCodeSchema.optional(),
  currencyConversionDate: CurrencyConversionDateSchema.optional(),
  phoneNumber: z.string().optional(),
  addressAttributes: z
    .object({
      streetName: z.string().min(1),
      streetNumber: z.string().min(1),
      streetBox: z.string().nullable(),
      zipCode: z.string().min(1),
      city: z.string().min(1),
      countryCode: z.string().min(1),
    })
    .optional(),
});

export type UpdateCompanyProfileVariables = z.infer<typeof UpdateCompanyProfileVariablesSchema>;

export async function updateCompanyProfileFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateCompanyProfileVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateCompanyProfileVariablesSchema.parse(variables));
}

export function useUpdateCompanyProfile() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateCompanyProfileVariables>({
    mutationFn: variables => updateCompanyProfileFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['companyProfile'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
      // The default language decides which row the communication languages card
      // locks, so that card is stale as soon as this one saves.
      client.invalidateQueries({ queryKey: ['communicationLocales'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateCompanyProfileVariables,
    'updateCompanyProfile'
  >('updateCompanyProfile', mutation);
}
